"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { Sparkles, User, Calendar, Clock, MapPin, Info, Lock } from "lucide-react";
import PaymentModal from "./PaymentModal";

export interface BirthFormData {
  name: string;
  birthdate: string;
  birthtime: string;
  birthplace: string;
}

interface BirthDataFormProps {
  onSubmit: (data: BirthFormData) => void;
  isLoading: boolean;
  payerEmail?: string | null;
  onReportGenerated?: () => void;
  isPaidFromEmail?: boolean;
}

export default function BirthDataForm({ onSubmit, isLoading, payerEmail, onReportGenerated, isPaidFromEmail }: BirthDataFormProps) {
  const [isPaid, setIsPaid] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [reportAlreadyGenerated, setReportAlreadyGenerated] = useState(false);

  // If user arrived via email receipt link with a confirmed payment, mark as paid
  const effectivelyPaid = isPaid || !!isPaidFromEmail;

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<BirthFormData>({
    defaultValues: {
      name: "",
      birthdate: "",
      birthtime: "",
      birthplace: ""
    }
  });

  const handleFormSubmit = (data: BirthFormData) => {
    if (!effectivelyPaid) {
      setIsPaymentModalOpen(true);
      return;
    }
    if (reportAlreadyGenerated) return;
    setReportAlreadyGenerated(true);
    onReportGenerated?.();
    onSubmit(data);
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Header Section */}
      <div className="text-center mb-12 fade-in-up">

        <div className="inline-block rounded-3xl px-8 py-6 mb-2" style={{ background: "linear-gradient(135deg, rgba(80,40,63,0.85) 0%, rgba(40,20,50,0.9) 50%, rgba(60,30,70,0.85) 100%)", border: "1px solid rgba(219,184,77,0.25)", backdropFilter: "blur(16px)", boxShadow: "0 8px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(219,184,77,0.15)" }}>
          <h1
            className="text-4xl md:text-5xl font-display mb-0 leading-tight"
            style={{ fontWeight: 800, fontFamily: "'Abril Fatface', cursive" }}>

            <span className="text-white/90">Discover Your </span>
            <span className="gold-gradient-text">Avatar Blueprint</span> <span className="text-white/90">& </span><span className="gold-gradient-text">Life Purpose</span>
          </h1>
        </div>
       
      </div>

      {/* Three Feature Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-10 fade-in-up stagger-1">

        {/* Card 1 */}
        <div className="rounded-2xl p-6 flex flex-col gap-3" style={{ background: "linear-gradient(to right, rgba(83, 22, 89, 0.97) 0%, rgba(100,45,70,0.80) 100%)", border: "1px solid rgba(219,184,77,0.18)", backdropFilter: "blur(12px)" }}>
          <h3 className="font-playfair text-lg leading-snug" style={{ color: "#dbb84d", fontWeight: 600 }}>
            Your Archetypal Essence
          </h3>
          <p className="text-sm leading-relaxed text-white/65" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            A concise blueprint that captures the essence of who you really are — a powerful reminder of your authentic path.
          </p>
        </div>

        {/* Card 2 */}
        <div className="rounded-2xl p-6 flex flex-col gap-3" style={{ background: "linear-gradient(to right, rgba(100,45,70,0.80) 0%, rgba(80,40,100,0.80) 100%)", border: "1px solid rgba(219,184,77,0.18)", backdropFilter: "blur(12px)" }}>
          <h3 className="font-playfair text-lg leading-snug" style={{ color: "#dbb84d", fontWeight: 600 }}>
            Your Avatar Identity
          </h3>
          <p className="text-sm leading-relaxed text-white/65" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            A deeper exploration of your design. Understand how your inner design interacts with the world around you.
          </p>
        </div>

        {/* Card 3 */}
        <div className="rounded-2xl p-6 flex flex-col gap-3" style={{ background: "linear-gradient(to right, rgba(80,40,100,0.80) 0%, rgba(49, 8, 57, 0.85) 100%)", border: "1px solid rgba(219,184,77,0.18)", backdropFilter: "blur(12px)" }}>
          <h3 className="font-playfair text-lg leading-snug" style={{ color: "#dbb84d", fontWeight: 600 }}>
            Your 30-Day Alignment Plan
          </h3>
          <p className="text-sm leading-relaxed text-white/65" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            It is not just knowing your path, but embodying it with a 30-day action plan to help you begin aligning your life with your Avatar.
          </p>
        </div>

      </div>

      {/* ── Payment CTA ── */}
      {!effectivelyPaid ? (
        <div className="fade-in-up stagger-2 mb-10">
          <div
            className="rounded-2xl p-8 text-center"
            style={{
              background: "linear-gradient(135deg, rgba(80,40,63,0.75) 0%, rgba(40,20,50,0.85) 100%)",
              border: "1px solid rgba(219,184,77,0.3)",
              backdropFilter: "blur(16px)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(219,184,77,0.12)",
            }}
          >
            <p
              className="text-sm text-white/55 mb-1"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              One-time payment
            </p>
            <p
              className="text-5xl font-bold mb-1"
              style={{ color: "#dbb84d", fontFamily: "'Abril Fatface', cursive" }}
            >
              $22
            </p>
            <p
              className="text-xs text-white/40 mb-6"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              USD · Instant access · No subscription
            </p>

            <button
              type="button"
              onClick={() => setIsPaymentModalOpen(true)}
              className="inline-flex items-center justify-center gap-3 px-10 py-4 rounded-xl font-semibold text-base transition-all"
              style={{
                background: "linear-gradient(135deg, #dbb84d 0%, #c9a43a 100%)",
                color: "#1a0a22",
                boxShadow: "0 4px 24px rgba(219,184,77,0.4)",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              <Lock className="w-5 h-5" />
              Unlock My Avatar Blueprint — $22
            </button>

            <p
              className="mt-4 text-xs text-white/35"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              🔒 Secured by Stripe · 256-bit SSL encryption
            </p>
          </div>
        </div>
      ) : (
        <div
          className="fade-in-up mb-8 rounded-xl px-5 py-3 flex items-center gap-3"
          style={{
            background: "rgba(34,197,94,0.08)",
            border: "1px solid rgba(34,197,94,0.25)",
          }}
        >
          <span style={{ color: "#22c55e", fontSize: "1.2rem" }}>✓</span>
          <p
            className="text-sm font-medium"
            style={{ color: "rgba(34,197,94,0.9)", fontFamily: "'DM Sans', sans-serif" }}
          >
            Payment confirmed — enter your birth data below to generate your report.
          </p>
        </div>
      )}

      {/* Form Card — always visible */}
      <div className="nautilus-card-elevated p-8 md:p-10 fade-in-up stagger-2">
        <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
          <div className="space-y-6">

            {/* Name Field */}
            <div>
              <label
                htmlFor="name"
                className="flex items-center gap-2 text-sm font-600 mb-2 text-white/80"
                style={{ fontWeight: 600 }}>

                <User className="w-4 h-4" style={{ color: "#dbb84d" }} />
                Full Name
              </label>
              <p className="text-xs text-white/40 mb-3" style={{ fontWeight: 400 }}>
                Your full name that appears on your birth certificate.
              </p>
              <input
                id="name"
                type="text"
                autoComplete="name"
                placeholder="e.g. Amara Osei"
                className={`form-input ${errors.name ? "error" : ""}`}
                {...register("name", {
                  required: "Your name is required to personalize the report.",
                  minLength: { value: 2, message: "Name must be at least 2 characters." },
                  maxLength: { value: 60, message: "Name must be under 60 characters." }
                })} />

              {errors.name &&
              <p className="mt-2 text-xs font-medium" style={{ color: "rgba(239,68,68,0.9)" }}>
                  {errors.name.message}
                </p>
              }
            </div>

            {/* Birth Date Field */}
            <div>
              <label
                htmlFor="birthdate"
                className="flex items-center gap-2 text-sm font-600 mb-2 text-white/80"
                style={{ fontWeight: 600 }}>

                <Calendar className="w-4 h-4" style={{ color: "#dbb84d" }} />
                Date of Birth
              </label>
              <p className="text-xs text-white/40 mb-3" style={{ fontWeight: 400 }}>
                Your exact birth date shapes your core archetypal pattern.
              </p>
              <input
                id="birthdate"
                type="date"
                className={`form-input ${errors.birthdate ? "error" : ""}`}
                style={{ colorScheme: "dark" }}
                {...register("birthdate", {
                  required: "Birth date is required.",
                  validate: (value) => {
                    const date = new Date(value);
                    const now = new Date();
                    if (date > now) return "Birth date cannot be in the future.";
                    const minDate = new Date("1900-01-01");
                    if (date < minDate) return "Please enter a valid birth date after 1900.";
                    return true;
                  }
                })} />

              {errors.birthdate &&
              <p className="mt-2 text-xs font-medium" style={{ color: "rgba(239,68,68,0.9)" }}>
                  {errors.birthdate.message}
                </p>
              }
            </div>

            {/* Birth Time Field */}
            <div>
              <label
                htmlFor="birthtime"
                className="flex items-center gap-2 text-sm font-600 mb-2 text-white/80"
                style={{ fontWeight: 600 }}>

                <Clock className="w-4 h-4" style={{ color: "#dbb84d" }} />
                Time of Birth
                <span
                  className="ml-1 text-xs px-2 py-0.5 rounded-full"
                  style={{
                    background: "rgba(219,184,77,0.12)",
                    color: "rgba(219,184,77,0.7)",
                    fontWeight: 500
                  }}>

                  Optional
                </span>
              </label>
              <p className="text-xs text-white/40 mb-3" style={{ fontWeight: 400 }}>
                If unknown, leave blank. Approximate times like "morning" or "noon" are fine to estimate.
              </p>
              <input
                id="birthtime"
                type="time"
                className="form-input"
                style={{ colorScheme: "dark" }}
                {...register("birthtime")} />

            </div>

            {/* Birth Location Field */}
            <div>
              <label
                htmlFor="birthplace"
                className="flex items-center gap-2 text-sm font-600 mb-2 text-white/80"
                style={{ fontWeight: 600 }}>

                <MapPin className="w-4 h-4" style={{ color: "#dbb84d" }} />
                Place of Birth
              </label>
              <p className="text-xs text-white/40 mb-3" style={{ fontWeight: 400 }}>
                City and country is ideal. State or region also works well.
              </p>
              <input
                id="birthplace"
                type="text"
                autoComplete="off"
                placeholder="e.g. Nairobi, Kenya"
                className={`form-input ${errors.birthplace ? "error" : ""}`}
                {...register("birthplace", {
                  required: "Birth location is required.",
                  minLength: { value: 2, message: "Please enter a valid location." },
                  maxLength: { value: 100, message: "Location must be under 100 characters." }
                })} />

              {errors.birthplace &&
              <p className="mt-2 text-xs font-medium" style={{ color: "rgba(239,68,68,0.9)" }}>
                  {errors.birthplace.message}
                </p>
              }
            </div>

            {/* Privacy Note */}
            <div
              className="flex items-start gap-3 p-4 rounded-xl"
              style={{
                background: "rgba(219,184,77,0.06)",
                border: "1px solid rgba(219,184,77,0.15)"
              }}>

              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "rgba(219,184,77,0.6)" }} />
              <p className="text-xs text-white/45 leading-relaxed" style={{ fontWeight: 400 }}>
                Your birth data is used only to generate your personal report. It is not stored, shared, or used for any other purpose.
              </p>
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              {reportAlreadyGenerated ? (
                <div
                  className="w-full flex items-center justify-center gap-3 py-4 rounded-xl font-semibold text-base"
                  style={{
                    background: "rgba(219,184,77,0.08)",
                    border: "1px solid rgba(219,184,77,0.2)",
                    color: "rgba(219,184,77,0.5)",
                    fontFamily: "'DM Sans', sans-serif",
                    cursor: "not-allowed",
                  }}
                >
                  <Sparkles className="w-5 h-5" />
                  <span>Report Already Generated</span>
                </div>
              ) : (
                <button
                  type="submit"
                  disabled={isLoading || !effectivelyPaid}
                  className="btn-primary w-full flex items-center justify-center gap-3"
                  style={{
                    background: !effectivelyPaid ? "rgba(219,184,77,0.3)" : "#ddbc55",
                    cursor: !effectivelyPaid ? "not-allowed" : isLoading ? "wait" : "pointer",
                  }}
                  title={!effectivelyPaid ? "Complete payment to generate your report" : undefined}
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin w-5 h-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      <span>Generating your Blueprint...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      <span>Generate My Avatar Blueprint</span>
                    </>
                  )}
                </button>
              )}
            </div>
            <p className="text-center text-xs text-gray-400 mt-3 px-[75px]">
              By clicking &quot;Generate My Avatar Blueprint&quot;, you agree to our{' '}
              <a href="https://quantume.earth/terms/" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-300 transition-colors">Terms and Conditions</a>
              {' '}and{' '}
              <a href="https://quantume.earth/privacy-policy/" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-300 transition-colors">Privacy &amp; Cookie</a> Policies.
            </p>

          </div>
        </form>
      </div>

      {/* Footer Note */}
      <p className="text-center text-xs text-white/50 mt-6 fade-in-up stagger-4" style={{ fontWeight: 400 }}>
        Your report is unique to you — generated from the intersection of your birth data, archetypal patterns, and life themes.
      </p>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSuccess={(email: string) => {
          setIsPaid(true);
          setIsPaymentModalOpen(false);
        }}
      />
    </div>
  );
}